import React, { Suspense, lazy } from "react";
import "./index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { getToken } from "./lib/session";
import { PageSkeleton } from "./components/Skeleton";

const page = (loader) => lazy(loader);
const Landing = page(() => import("./pages/Landing"));
const Login = page(() => import("./pages/Login"));
const Signup = page(() => import("./pages/Signup"));
const TeacherLogin = page(() => import("./pages/TeacherLogin"));
const TeacherSetPassword = page(() => import("./pages/TeacherSetPassword"));
const Dashboard = page(() => import("./pages/Dashboard"));
const Students = page(() => import("./pages/Students"));
const Fees = page(() => import("./pages/Fees"));
const Attendance = page(() => import("./pages/Attendance"));
const Batches = page(() => import("./pages/Batches"));
const Teachers = page(() => import("./pages/Teachers"));
const Leads = page(() => import("./pages/Leads"));
const Expenses = page(() => import("./pages/Expenses"));
const Reports = page(() => import("./pages/Reports"));
const Tests = page(() => import("./pages/Tests"));
const Settings = page(() => import("./pages/Settings"));
const SuperAdmin = page(() => import("./pages/SuperAdmin"));
const TeacherDashboard = page(() => import("./pages/TeacherDashboard"));
const Layout = page(() => import("./components/Layout"));
const LegalPage = page(() => import("./pages/LegalPage"));
const ReportCard = page(() => import("./pages/ReportCard"));
const PublicReceipt = page(() => import("./pages/PublicReceipt"));
const ForgotPassword = page(() => import("./pages/ForgotPassword"));
const ResetPassword = page(() => import("./pages/ResetPassword"));

function Protected({ children, role = "owner" }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-6 max-w-6xl mx-auto"><PageSkeleton /></div>;
  if (!user) {
    // No session for this portal: if the other portal is signed in, send them home instead of erroring.
    if (role === "teacher") return <Navigate to={getToken("owner") ? "/dashboard" : "/teacher-login"} replace />;
    return <Navigate to={getToken("teacher") ? "/teacher-dashboard" : "/login"} replace />;
  }
  if (role === "owner" && !["owner", "super_admin"].includes(user.role)) return <Navigate to="/teacher-dashboard" replace />;
  if (role === "teacher" && user.role !== "teacher") return <Navigate to="/dashboard" replace />;
  if (role === "super_admin" && user.role !== "super_admin") return <Navigate to="/" replace />;
  return children;
}

const Fallback = () => <div className="p-6 max-w-6xl mx-auto"><PageSkeleton /></div>;

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster richColors position="top-right" />
        <Suspense fallback={<Fallback />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/teacher-login" element={<TeacherLogin />} />
            <Route path="/teacher-set-password" element={<TeacherSetPassword />} />
            <Route path="/privacy" element={<LegalPage kind="privacy" />} />
            <Route path="/terms" element={<LegalPage kind="terms" />} />
            <Route path="/refund" element={<LegalPage kind="refund" />} />
            <Route path="/report/:token" element={<ReportCard />} />
            <Route path="/receipt/subscription/:token" element={<PublicReceipt />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/teacher-forgot-password" element={<ForgotPassword portal="teacher" />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/students" element={<Students />} />
              <Route path="/fees" element={<Fees />} />
              <Route path="/attendance" element={<Attendance />} />
              <Route path="/batches" element={<Batches />} />
              <Route path="/teachers" element={<Teachers />} />
              <Route path="/leads" element={<Leads />} />
              <Route path="/expenses" element={<Expenses />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/tests" element={<Tests />} />
              <Route path="/settings" element={<Settings />} />
            </Route>
            <Route path="/teacher-dashboard" element={<Protected role="teacher"><TeacherDashboard /></Protected>} />
            <Route path="/super-admin" element={<Protected role="super_admin"><SuperAdmin /></Protected>} />
            <Route path="/admin" element={<Protected role="super_admin"><SuperAdmin /></Protected>} />
            <Route path="/admin/login" element={<Login />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
